import {
  FOC_TOUR_LABEL_NOTIFICATIONS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
} from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import { CorporateViewTabs } from '../hubNavTabs';

export const FOC_NOTIFICATIONS_TOUR_PARAM = 'focNotificationsTour';

/** Opens Hub Notifications and starts the 6.1 Notification Management walkthrough. */
export const FOC_NOTIFICATIONS_TOUR_HREF = `/hub?openTab=${CorporateViewTabs.Notifications}&${FOC_NOTIFICATIONS_TOUR_PARAM}=1`;

export type FocNotificationsTourSurface =
  | 'list'
  | 'compose'
  | 'automation'
  | 'delivery';

export type FocNotificationsTourStepId =
  | 'sectionIntro'
  | 'us001'
  | 'us002'
  | 'us003'
  | 'us004'
  | 'us005'
  | 'us000'
  | 'us007'
  | 'us008'
  | 'us009'
  | 'us006'
  | 'us014'
  | 'us011'
  | 'us015'
  | 'us013'
  | 'us016'
  | 'not001'
  | 'not002'
  | 'not003'
  | 'focFeedback';

export type FocNotificationsTourStepDef = {
  id: FocNotificationsTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
  surface?: FocNotificationsTourSurface;
};

type RequirementInput = Omit<
  FocNotificationsTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocNotificationsTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk for 6.1 Notification Management: US-000–016 (carryover) and
 * FOC-NOT-001–003, ending with FOC Feedback Notes.
 */
export const FOC_NOTIFICATIONS_TOUR_STEPS: FocNotificationsTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'list',
    tour: 'notifications-workspace',
    title: '6.1 Notification Management',
    description:
      'Manage incident and organizational notifications on the Hub COP: compose multi-channel alerts, track acknowledgements, and review delivery history.',
  },
  requirement({
    id: 'us001',
    requirementId: 'US-001',
    name: 'View Active Notifications List',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'notifications-active-list',
    description:
      'View a list of all active notifications sorted by severity so the most critical information appears first.',
  }),
  requirement({
    id: 'us002',
    requirementId: 'US-002',
    name: 'View Active Notification Count (Badge)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'hub-tab-notifications',
    description:
      'See a visual indicator (badge) with the current number of active notifications so you stay aware even when you are not on the Notifications tab.',
  }),
  requirement({
    id: 'us003',
    requirementId: 'US-003',
    name: 'Expand Notification Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'notifications-expand',
    description:
      'Click to expand a collapsed notification to view the full message, recipients, attachments, and actions without cluttering the main list.',
  }),
  requirement({
    id: 'us004',
    requirementId: 'US-004',
    name: 'Toggle Active vs. Historical Views',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'notifications-filter',
    description:
      'Toggle between Active and Historical lists so you can focus on current tasks or review past communications.',
  }),
  requirement({
    id: 'us005',
    requirementId: 'US-005',
    name: 'Manually Compose Notification',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-compose',
    description:
      'Manually create a new notification with a custom title, severity, and message to communicate intentional information to the team.',
  }),
  requirement({
    id: 'us000',
    requirementId: 'US-000',
    name: 'Stand Down Safety Notifications',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-stand-down',
    description:
      'When Severity is Stand Down Safety, the compose drawer explains that the alert will be sent immediately via available channels, and Scheduled Time is locked to send now.',
  }),
  requirement({
    id: 'us007',
    requirementId: 'US-007',
    name: 'Select Channels and Customize Messages',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-channels',
    description:
      'Select one or more delivery methods (PRATUS, Email, SMS, and other channels) and use the same message across all or customize the body for each channel.',
  }),
  requirement({
    id: 'us008',
    requirementId: 'US-008',
    name: 'Schedule Notification Delivery',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-schedule',
    description:
      'Choose between sending a notification immediately or scheduling it for a future time so you can prepare communications in advance.',
  }),
  requirement({
    id: 'us009',
    requirementId: 'US-009',
    name: 'Target Recipients',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-recipients',
    description:
      'Select specific individuals or defined teams as recipients so the message is targeted effectively.',
  }),
  requirement({
    id: 'us006',
    requirementId: 'US-006',
    name: 'Attach Documents',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-attachments',
    description:
      'Attach files or folders from the organization or workspace File Manager so recipients receive necessary context with the alert.',
  }),
  requirement({
    id: 'us014',
    requirementId: 'US-014',
    name: 'Link Notification to Map Point or Area',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-map-area',
    description:
      'Link a notification to a geographic point or defined area on the map so recipients can visualize the location associated with the alert.',
  }),
  requirement({
    id: 'us011',
    requirementId: 'US-011',
    name: 'Acknowledge Notification',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'notifications-acknowledge',
    description:
      'Acknowledge receipt of a critical notification with a single click so the sender knows you have received the information.',
  }),
  requirement({
    id: 'us015',
    requirementId: 'US-015',
    name: 'Auto-Archive Completed Notifications',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'notifications-filter',
    description:
      'After you acknowledge receipt or complete the required action, notifications move from Active to Historical so your active list stays focused on pending tasks.',
  }),
  requirement({
    id: 'us013',
    requirementId: 'US-013',
    name: 'Zoom to Notification Location',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'list',
    tour: 'notifications-zoom',
    description:
      'Use the map control on a location-aligned notification so the map can focus on the relevant area. (Map fly-to wiring continues to mature.)',
  }),
  requirement({
    id: 'us016',
    requirementId: 'US-016',
    name: 'View Notification Response/Acknowledgement Summary',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'delivery',
    tour: 'notifications-delivery-report',
    description:
      'As a notification sender, open Delivery Report to view a summary of delivery and individual responses for notifications you have sent.',
  }),
  requirement({
    id: 'not001',
    requirementId: 'FOC-NOT-001',
    name: 'Permission-Aware Notification Delivery',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'compose',
    tour: 'notifications-recipients',
    description:
      'PRATUS sends notifications only through authorized communication pathways and only to users authorized to receive the associated incident, workspace, form, map, resource, financial, or restricted information.',
  }),
  requirement({
    id: 'not002',
    requirementId: 'FOC-NOT-002',
    name: 'Workflow-Generated Notifications',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'automation',
    tour: 'notifications-automation',
    description:
      'PRATUS generates configurable notifications when an authorized user assigns work, requests review, submits a product for approval, changes a required product status, identifies a blocked dependency, routes a resource request, changes a meeting status, or publishes an approved incident product. Configure automation notify settings here.',
  }),
  requirement({
    id: 'not003',
    requirementId: 'FOC-NOT-003',
    name: 'Notification Audit Trail',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'delivery',
    tour: 'notifications-delivery-report',
    description:
      'PRATUS records the notification sender or system trigger, recipients, delivery channel, time sent, related incident/workspace, related record, acknowledgement status, and relevant response status.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_NOTIFICATIONS),
  },
];

export function focNotificationsTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focNotificationsTourStepIds(): FocNotificationsTourStepId[] {
  return FOC_NOTIFICATIONS_TOUR_STEPS.map((step) => step.id);
}

export function focNotificationsTourStepTitles(): string[] {
  return FOC_NOTIFICATIONS_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function focNotificationsTourPopoverHtml(
  step: Pick<
    FocNotificationsTourStepDef,
    'disposition' | 'description' | 'bullets'
  >
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
